<template>
  <div class="dlg">
    <header class="dlg__head">
      <div class="dlg__eyebrow">
        <span class="dlg__dot"></span>
        WAIZMANNVERGLEICH / BEITRAGSENTWICKLUNG
      </div>
      <h2 class="dlg__title">
        Beitragsentwicklung: {{ tarif.un_name }}
        <span v-if="tarif.tarif_title" class="be-title-sub">
          · {{ tarif.tarif_title }}
        </span>
      </h2>
      <p class="dlg__sub">
        So entwickeln sich die monatlichen Beiträge in den nächsten 10 Jahren.
        (Außerordentliche Beitragsanpassungen ausgenommen.)
      </p>
    </header>

    <div
      v-if="tarif.tarif_altersrueckstellung == 0"
      class="dlg__note dlg__note--ok"
    >
      <v-icon icon="mdi-check-circle-outline" size="16" />
      <span>
        Die Beiträge der <strong>{{ tarif.un_name }} {{ tarif.tarif_title }}</strong>
        sind <strong>mit</strong> Altersrückstellungen kalkuliert. Dadurch
        bleiben die Beiträge nach Abschluss stabil.
      </span>
    </div>
    <div v-else class="dlg__note dlg__note--warn">
      <v-icon icon="mdi-alert-circle-outline" size="16" />
      <span>
        Die Beiträge der <strong>{{ tarif.un_name }} {{ tarif.tarif_title }}</strong>
        sind <strong>ohne</strong> Altersrückstellungen kalkuliert. Die Beiträge
        werden regelmäßig entsprechend Ihres Alters angepasst.
      </span>
    </div>

    <div v-if="preisSpruenge" class="dlg__section">
      <h3 class="dlg__section-title">Beitragssprünge nach Alter</h3>
      <div class="dlg__table-wrap">
        <table class="dlg__table">
          <thead>
            <tr>
              <th>Ab Alter</th>
              <th class="dlg__th-num">monatlicher Beitrag</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in preisSpruenge" :key="p.preis_alter">
              <td>{{ p.preis_alter }} Jahre</td>
              <td class="dlg__td-num">{{ p.preis_value }} €</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="dlg__section">
      <h3 class="dlg__section-title">Die Beitragsentwicklung im Vergleich</h3>
      <div class="dlg__table-wrap">
        <table class="dlg__table">
          <thead>
            <tr>
              <th>Tarif</th>
              <th class="dlg__th-num">Heute</th>
              <th class="dlg__th-num">in 1 Jahr</th>
              <th class="dlg__th-num">in 5 Jahren</th>
              <th class="dlg__th-num">in 10 Jahren</th>
              <th>Angebot</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="tarif.tarif_id" class="is-self">
              <td class="dlg__td-tarif">
                <router-link v-if="tarif.antraglink" :to="tarif.antraglink">
                  {{ tarif.un_name }}
                </router-link>
                <span v-else>{{ tarif.un_name }}</span>
                <span v-if="tarif.empfehlung_type > 0" class="dlg__tipp">
                  Tipp
                </span>
                <div class="dlg__td-tarif-sub">{{ tarif.tarif_title }}</div>
              </td>
              <td class="dlg__td-num">{{ tarif.preis_value }} €</td>
              <td class="dlg__td-num">{{ tarif.preis_value_plus_1 }} €</td>
              <td class="dlg__td-num">{{ tarif.preis_value_plus_5 }} €</td>
              <td class="dlg__td-num">{{ tarif.preis_value_plus_10 }} €</td>
              <td>
                <router-link
                  v-if="tarif.antraglink"
                  :to="tarif.antraglink"
                  class="dlg__btn"
                >
                  Angebot
                </router-link>
              </td>
            </tr>
            <tr
              v-for="item in formattedTarife"
              :key="item.tarif_id"
            >
              <td class="dlg__td-tarif">
                <router-link v-if="item.antraglink" :to="item.antraglink">
                  {{ item.un_name }}
                </router-link>
                <span v-else>{{ item.un_name }}</span>
                <span v-if="item.empfehlung_type > 0" class="dlg__tipp">
                  Tipp
                </span>
                <div class="dlg__td-tarif-sub">{{ item.tarif_title }}</div>
              </td>
              <td class="dlg__td-num">{{ item.preis_value }} €</td>
              <td class="dlg__td-num">{{ item.preis_value_plus_1 }} €</td>
              <td class="dlg__td-num">{{ item.preis_value_plus_5 }} €</td>
              <td class="dlg__td-num">{{ item.preis_value_plus_10 }} €</td>
              <td>
                <router-link
                  v-if="item.antraglink"
                  :to="item.antraglink"
                  class="dlg__btn dlg__btn--ghost"
                >
                  Angebot
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, defineProps, onMounted } from "vue";
  import BaseCalls from "@/services/BaseCalls";
  const props = defineProps({
    alletarife: Array,
    tarif: {
      type: Object,
      default: () => ({}),
    },
    berechnung: {
      type: Object,
      default: () => ({}),
    },
  });
  const preisSpruenge = ref(false);
  const formattedTarife = computed(() => {
    if (props.tarif) {
      return props.alletarife.map((tarif) => ({
        ...tarif,
        antraglink: tarif.antraglink || "#",
      }));
    } else {
      return props.alletarife;
    }
  });
  async function getBeitragsEntwicklung() {
    let select = {};
    select.action = "getTarifPreisSpruenge";
    select.tarif_id = props.tarif.tarif_id;

    try {
      const resp = await BaseCalls.postTarif(select);
      if (resp.data && resp.data.preisSpruenge) {
        preisSpruenge.value = resp.data.preisSpruenge;
      }
    } catch (err) {
      console.log("error berechnung");
    }
  }
  onMounted(() => {
    if (props.tarif.tarif_altersrueckstellung == 1) {
      getBeitragsEntwicklung();
    }
  });
</script>

<style scoped>
  .be-title-sub {
    font-weight: 500;
    color: #6b7280;
    font-size: 0.85em;
  }
</style>
